import type { FC } from 'react';
import React from 'react';
import type { BoxProps } from '@withjoy/joykit';
import { Box, styled } from '@withjoy/joykit';
import { Play } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import type { VideoStallButtonPlacement } from './VideoStallPlayButton.types';
import { getStallGlyphColor, STALL_BUTTON_BACKING } from './VideoStallPlayButton.utils';

type VideoStallPlayButtonProps = Readonly<{
  placement: VideoStallButtonPlacement;
  /** True while a tap is waiting for playback; the button stays visible but ignores further taps. */
  isRetrying: boolean;
  /** Must call `playAfterStall` synchronously so `play()` runs inside the guest's gesture. */
  onPlay: () => void;
}>;

const BUTTON_SIZE = pxToRem(44);
const EDGE_INSET = pxToRem(16);
// On mobile the BouncingChevron straddles the frame's bottom edge; sit above its row.
const BOTTOM_INSET = { _: pxToRem(32), sm2: pxToRem(24) };

const PLACEMENT_POSITION: Readonly<Record<VideoStallButtonPlacement, Pick<BoxProps, 'top' | 'bottom' | 'left' | 'right'>>> = {
  bottomRight: { bottom: BOTTOM_INSET, right: EDGE_INSET },
  bottomLeft: { bottom: BOTTOM_INSET, left: EDGE_INSET },
  topRight: { top: EDGE_INSET, right: EDGE_INSET },
};

/* Rendered after the renderer's content layer (zIndex banner), which covers the whole frame
   and takes pointer events — anything lower is unclickable. */
const StallButton = styled(Box)`
  z-index: ${({ theme }) => Number(theme.zIndices.banner) + 1};
`;

/**
 * Icon-only tap-to-play button shown over the poster when a header video's autoplay stalls.
 * Glyph uses the event accent colour (the RSVP button's source) on a light translucent backing,
 * because it sits on the couple's own poster and a dark accent would vanish on a dark photo. A
 * pale accent that wouldn't read on that backing falls back to a neutral dark token.
 */
export const VideoStallPlayButton: FC<VideoStallPlayButtonProps> = ({ placement, isRetrying, onPlay }) => {
  const { tq } = useTranslation('videoBackground');
  const { accentColor, rsvpButtonColor } = useColorPaletteProvider();

  return (
    <StallButton
      as="button"
      type="button"
      aria-label={tq`Play Video`}
      disabled={isRetrying}
      onClick={onPlay}
      position="absolute"
      {...PLACEMENT_POSITION[placement]}
      width={BUTTON_SIZE}
      height={BUTTON_SIZE}
      display="flex"
      alignItems="center"
      justifyContent="center"
      padding={0}
      border="none"
      borderRadius="50%"
      backgroundColor={STALL_BUTTON_BACKING}
      color={getStallGlyphColor(accentColor || rsvpButtonColor)}
      cursor={isRetrying ? 'default' : 'pointer'}
      _focusVisible={{ outline: '2px solid', outlineColor: 'inputBorderFocused', outlineOffset: '2px' }}
    >
      <Play
        size={20}
        aria-hidden="true"
      />
    </StallButton>
  );
};
